import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Routes, Route } from 'react-router-dom';
import { saudiService } from '../../services/saudiService';
import MetricCard from '../../components/shared/MetricCard';
import PlotlyChart from '../../components/charts/PlotlyChart';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const COLORS = { Saudi: '#c9a96e', GCC: '#6baed6', Foreign: '#e07b54' };

const fmt = (val, suffix = '') => {
  if (val === null || val === undefined) return '-';
  const abs = Math.abs(val);
  if (abs >= 1e12) return `${(val / 1e12).toFixed(1)}T${suffix}`;
  if (abs >= 1e9) return `${(val / 1e9).toFixed(1)}B${suffix}`;
  if (abs >= 1e6) return `${(val / 1e6).toFixed(1)}M${suffix}`;
  if (abs >= 1e3) return `${(val / 1e3).toFixed(1)}K${suffix}`;
  return `${val.toFixed(0)}${suffix}`;
};
const fmtSAR = (v) => fmt(v, ' SAR');
const fmtPct = (v) => v == null ? '-' : `${v.toFixed(2)}%`;
const netColor = (v) => v == null ? undefined : v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

const subTabs = [
  { to: '/saudi/ownership', label: 'Snapshot', end: true },
  { to: '/saudi/ownership/trend', label: 'Trend' },
  { to: '/saudi/ownership/change', label: 'Change' },
  { to: '/saudi/ownership/institutional', label: 'Institutional' },
];

/* ========== Snapshot ========== */
const SnapshotView = ({ filterParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await saudiService.getOwnershipSnapshot(filterParams); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [filterParams]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading snapshot..." />;
  if (!data) return <EmptyState />;

  // API: { report_date, prev_date, total_holding, by_nationality: [{nationality, holding_value, free_float_pct, issued_pct, holding_delta, free_float_delta}], detail: [...] }
  const byNat = {};
  (data.by_nationality || []).forEach(r => { byNat[r.nationality] = r; });

  // Donut data
  const donutLabels = (data.by_nationality || []).map(r => r.nationality);
  const donutValues = (data.by_nationality || []).map(r => r.holding_value);
  const donutColors = donutLabels.map(l => COLORS[l] || '#888');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total Holding" value={fmtSAR(data.total_holding)} />
        <MetricCard label="Foreign Holding" value={fmtSAR(byNat.Foreign?.holding_value)}
          subtitle={`WoW: ${byNat.Foreign?.holding_delta >= 0 ? '+' : ''}${fmt(byNat.Foreign?.holding_delta)}`} />
        <MetricCard label="Foreign Free Float %" value={fmtPct(byNat.Foreign?.free_float_pct)}
          subtitle={`WoW: ${byNat.Foreign?.free_float_delta >= 0 ? '+' : ''}${byNat.Foreign?.free_float_delta?.toFixed(2) || '-'}%`} />
        <MetricCard label="Report Date" value={data.report_date || '-'} subtitle={data.prev_date ? `Prior: ${data.prev_date}` : undefined} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Ownership by Nationality</h4>
          <PlotlyChart
            data={[{ labels: donutLabels, values: donutValues, type: 'pie', hole: 0.55,
              marker: { colors: donutColors }, textinfo: 'label+percent', textposition: 'outside',
              hovertemplate: '%{label}<br>%{value:,.0f} SAR<br>%{percent}<extra></extra>' }]}
            layout={{ showlegend: false, margin: { l: 20, r: 20, t: 10, b: 20 } }}
            style={{ minHeight: 280 }}
          />
        </div>

        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Detail by Investor Type</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Nationality</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Type</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Holding</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">FF%</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Issued%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {(data.detail || []).map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{r.nationality}</td>
                    <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.investor_type}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtSAR(r.holding_value)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtPct(r.free_float_pct)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtPct(r.issued_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ========== Trend ========== */
const TrendView = ({ filterParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await saudiService.getOwnershipTrend(filterParams); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [filterParams]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading trend..." />;
  if (!data || !Array.isArray(data) || data.length === 0) return <EmptyState />;

  // API returns flat: [{report_date, nationality, holding_value, free_float_pct, issued_pct}]
  const byDate = {};
  data.forEach(r => { if (!byDate[r.report_date]) byDate[r.report_date] = {}; byDate[r.report_date][r.nationality] = r; });
  const weeks = Object.keys(byDate).sort();
  const wl = weeks.map(w => new Date(w + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit' }));
  const nats = ['Saudi', 'GCC', 'Foreign'];

  const ffTraces = nats.map(nat => ({
    x: wl, y: weeks.map(w => byDate[w]?.[nat]?.free_float_pct || 0),
    name: nat, type: 'scatter', mode: 'lines+markers',
    line: { color: COLORS[nat], width: 2 }, marker: { size: 5 },
    hovertemplate: `${nat}: %{y:.2f}%<extra></extra>`,
  }));

  const holdTraces = nats.map(nat => ({
    x: wl, y: weeks.map(w => byDate[w]?.[nat]?.holding_value || 0),
    name: nat, type: 'scatter', mode: 'lines+markers',
    line: { color: COLORS[nat], width: 2 }, marker: { size: 5 },
    hovertemplate: `${nat}: %{customdata}<extra></extra>`,
    customdata: weeks.map(w => fmtSAR(byDate[w]?.[nat]?.holding_value || 0)),
  }));

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Free Float % by Nationality</h4>
        <PlotlyChart data={ffTraces} layout={{
          xaxis: { type: 'category', tickfont: { size: 10 } },
          yaxis: { ticksuffix: '%', tickfont: { size: 10 } },
          legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0 },
        }} />
      </div>
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Holding Value by Nationality</h4>
        <PlotlyChart data={holdTraces} layout={{
          xaxis: { type: 'category', tickfont: { size: 10 } },
          yaxis: { tickformat: '.2s', tickfont: { size: 10 } },
          legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0 },
        }} />
      </div>
    </div>
  );
};

/* ========== Change ========== */
const ChangeView = ({ filterParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await saudiService.getOwnershipChange(filterParams); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [filterParams]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading changes..." />;
  if (!data) return <EmptyState />;

  // API: { weekly_delta: [{report_date, nationality, holding_value, delta, delta_pct}], detail: [...] }
  const deltas = data.weekly_delta || [];
  const byDate = {};
  deltas.forEach(r => { if (!byDate[r.report_date]) byDate[r.report_date] = {}; byDate[r.report_date][r.nationality] = r; });
  const weeks = Object.keys(byDate).sort();
  const wl = weeks.map(w => new Date(w + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit' }));
  const nats = ['Saudi', 'GCC', 'Foreign'];

  const deltaTraces = nats.map(nat => ({
    x: wl, y: weeks.map(w => byDate[w]?.[nat]?.delta || 0),
    name: nat, type: 'bar', marker: { color: COLORS[nat] },
    hovertemplate: `${nat}: %{customdata}<extra></extra>`,
    customdata: weeks.map(w => fmtSAR(byDate[w]?.[nat]?.delta || 0)),
  }));

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Week-on-Week Holding Change</h4>
        <PlotlyChart data={deltaTraces} layout={{
          barmode: 'group',
          xaxis: { type: 'category', tickfont: { size: 10 } },
          yaxis: { tickformat: '.2s', tickfont: { size: 10 } },
          legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0 },
          shapes: [{ type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0, line: { color: '#666', width: 1, dash: 'dot' } }],
        }} />
      </div>
    </div>
  );
};

/* ========== Institutional ========== */
const InstitutionalView = ({ filterParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await saudiService.getOwnershipInstitutional(filterParams); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [filterParams]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading institutional data..." />;
  if (!data || !Array.isArray(data) || data.length === 0) return <EmptyState />;

  // API returns flat: [{report_date, nationality, investor_type, holding_value, free_float_pct}]
  // Group by date → show per-nationality holding breakdown over time
  const byDate = {};
  data.forEach(r => {
    if (!byDate[r.report_date]) byDate[r.report_date] = {};
    const key = `${r.nationality}-${r.investor_type}`;
    byDate[r.report_date][key] = r;
  });
  const weeks = Object.keys(byDate).sort();
  const wl = weeks.map(w => new Date(w + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit' }));

  // Get unique keys from latest week
  const allKeys = [...new Set(data.map(r => `${r.nationality}-${r.investor_type}`))];
  const typeColors = [
    '#c9a96e','#d4a853','#b8954f','#a6823a','#947026','#826012','#6f5000','#5d4000',
    '#6baed6','#4a9ec9','#2b8ebb','#22c55e','#16a34a','#0d9488','#0891b2','#6366f1',
  ];

  const traces = allKeys.map((key, i) => ({
    x: wl, y: weeks.map(w => byDate[w]?.[key]?.holding_value || 0),
    name: key, type: 'scatter', mode: 'lines',
    line: { color: typeColors[i % typeColors.length], width: 1.5 },
    hovertemplate: `${key}: %{customdata}<extra></extra>`,
    customdata: weeks.map(w => fmtSAR(byDate[w]?.[key]?.holding_value || 0)),
  }));

  // Latest week table
  const latestWeek = weeks[weeks.length - 1];
  const latestData = latestWeek ? Object.values(byDate[latestWeek] || {}).sort((a, b) => b.holding_value - a.holding_value) : [];
  const totalHolding = latestData.reduce((s, r) => s + (r.holding_value || 0), 0);

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Holding by Investor Type Over Time</h4>
        <PlotlyChart data={traces} layout={{
          xaxis: { type: 'category', tickfont: { size: 10 } },
          yaxis: { tickformat: '.2s', tickfont: { size: 10 } },
          legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0, font: { size: 8 } },
        }} style={{ minHeight: 400 }} />
      </div>

      {latestData.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Latest Week — All Investor Types</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Nationality</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Investor Type</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Holding (SAR)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">FF%</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">% of Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {latestData.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{r.nationality}</td>
                    <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.investor_type}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtSAR(r.holding_value)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtPct(r.free_float_pct)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{totalHolding > 0 ? fmtPct(r.holding_value / totalHolding * 100) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ========== Empty / Main ========== */
const EmptyState = () => (
  <div className="text-center py-16 text-gray-500 dark:text-gray-400">
    <div className="text-4xl mb-3 opacity-30">&#x1f4ca;</div>
    <p className="text-sm">No ownership data available.</p>
  </div>
);

const OwnershipPage = ({ filterParams }) => (
  <div>
    <div className="flex overflow-x-auto gap-1 mb-4">
      {subTabs.map(tab => (
        <NavLink key={tab.to} to={tab.to} end={tab.end}
          className={({ isActive }) => `px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
            isActive ? 'bg-[#c9a96e]/10 text-[#c9a96e] border border-[#c9a96e]/30'
              : 'text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-[#c9a96e]/30'}`}
        >{tab.label}</NavLink>
      ))}
    </div>
    <Routes>
      <Route index element={<SnapshotView filterParams={filterParams} />} />
      <Route path="trend" element={<TrendView filterParams={filterParams} />} />
      <Route path="change" element={<ChangeView filterParams={filterParams} />} />
      <Route path="institutional" element={<InstitutionalView filterParams={filterParams} />} />
    </Routes>
  </div>
);

export default OwnershipPage;
